/**
 * Contesto dell'assistente: se c'e per questo utente, come si apre e quale
 * conversazione si sta facendo.
 *
 * Il pannello sta sopra ogni vista (§1.5), quindi vive una volta sola in cima
 * all'applicazione; la barra inferiore e il piede del menu si limitano ad
 * aprirlo. La conversazione in corso sta qui e non dentro il pannello, per
 * due ragioni: chiudere il pannello non la perde piu, e la vista a tutta
 * pagina (V59) continua la stessa, senza copie che divergono.
 */

import { createContext, useContext } from 'react';

export interface MessaggioConversazione {
  id: string;
  ruolo: 'user' | 'assistant' | 'tool';
  testo: string | null;
  creatoIl: string;
  /** Su un messaggio dell'assistente: le letture che ha chiesto. */
  chiamate?: { id: string; funzione: string }[];
  /** Su una risposta: i numeri del testo che non tornano con i dati, da verificare. */
  numeriDaVerificare?: string[];
  /** Su un risultato: la funzione e i dati che l'assistente ha letto. */
  funzione?: string;
  dati?: unknown;
}

export interface EsitoMessaggio {
  conversazioneId: string;
  messaggi: MessaggioConversazione[];
}

/** La squadra-stagione in cui una conversazione e nata, se ne aveva una. */
export interface SquadraConversazione {
  id: string;
  nome: string;
  stagione: string;
}

export interface VoceConversazione {
  id: string;
  titolo: string | null;
  squadra: SquadraConversazione | null;
  domande: number;
  creatoIl: string;
  aggiornatoIl: string;
}

export interface PaginaConversazioni {
  voci: VoceConversazione[];
  /** Quante passano i filtri, in tutte le pagine. */
  totale: number;
  /** Le squadre di tutte le proprie conversazioni, per il filtro. */
  squadre: SquadraConversazione[];
  senzaSquadra: boolean;
}

export interface ConversazioneLetta {
  id: string;
  titolo: string | null;
  squadraStagioneId: string | null;
  squadra: SquadraConversazione | null;
  creatoIl: string;
  aggiornatoIl: string;
  messaggi: MessaggioConversazione[];
}

/**
 * La conversazione in corso. `id` e null finche la prima domanda non l'ha
 * creata sul server; `squadra` serve alla vista a tutta pagina, dove il
 * percorso non dice quale squadra sia.
 */
export interface Conversazione {
  id: string | null;
  messaggi: MessaggioConversazione[];
  squadra?: SquadraConversazione | null;
}

export const NESSUNA: Conversazione = { id: null, messaggi: [] };

export type StatoAssistente =
  { disponibile: false } | { disponibile: true; domande: { usate: number; massimo: number } };

export interface ContestoAssistente {
  /** Null finche lo stato non e arrivato: i pulsanti non compaiono. */
  stato: StatoAssistente | null;
  /** Apre il pannello sulla conversazione in corso. */
  apri: () => void;
  chiudi: () => void;
  conversazione: Conversazione;
  imposta: (c: Conversazione) => void;
  /** Ricomincia da capo: «Nuova», o la conversazione in corso eliminata. */
  dimentica: () => void;
  aggiornaStato: () => Promise<void>;
  /**
   * Le letture e i calcoli sotto le risposte: utili a chi sviluppa, di troppo
   * in una demo. Nascosti finché non si chiede di vederli, per tutta la sessione.
   */
  mostraLetture: boolean;
  impostaLetture: (mostra: boolean) => void;
}

export const ContestoAi = createContext<ContestoAssistente | null>(null);

export function useAssistente(): ContestoAssistente {
  const valore = useContext(ContestoAi);
  if (!valore) throw new Error('useAssistente va usato dentro ProviderAssistente.');
  return valore;
}

/** Vero quando l'assistente c'e: i pulsanti si mostrano solo allora. */
export const assistenteDisponibile = (stato: StatoAssistente | null): boolean =>
  stato?.disponibile === true;
